import axios from 'axios';

/**
 * Centralized API Service for Form Submissions
 * Handles GeoLocation and Lead Submission to CRM
 */

// Dial code to country code mapping
const DIAL_CODE_TO_COUNTRY = {
  '+1': 'US',
  '+44': 'GB',
  '+91': 'IN',
  '+61': 'AU',
  '+86': 'CN',
  '+81': 'JP',
  '+49': 'DE',
  '+33': 'FR',
  '+39': 'IT',
  '+34': 'ES',
  '+7': 'RU',
  '+55': 'BR',
  '+52': 'MX',
  '+82': 'KR',
  '+31': 'NL',
  '+46': 'SE',
  '+41': 'CH',
  '+47': 'NO',
  '+45': 'DK',
  '+48': 'PL',
  '+65': 'SG',
  '+60': 'MY',
  '+66': 'TH',
  '+84': 'VN',
  '+62': 'ID',
  '+63': 'PH',
  '+92': 'PK',
  '+880': 'BD',
  '+971': 'AE',
  '+966': 'SA',
  '+27': 'ZA',
  '+234': 'NG',
  '+20': 'EG',
  '+254': 'KE',
  '+64': 'NZ',
  '+353': 'IE',
  '+351': 'PT',
  '+30': 'GR',
  '+32': 'BE',
  '+43': 'AT',
  '+358': 'FI'
};

/**
 * Get user's geolocation data from API
 * @returns {Promise<Object|null>} Geolocation data or null if failed
 */
export const getGeoLocation = async () => {
  try {
    const response = await axios.get(
      'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation',
      {
        headers: {
          'accept': 'text/plain'
        }
      }
    );
    
    if (response.data && response.data.isSuccess) {
      console.log('✅ GeoLocation fetched:', response.data.data);
      return response.data;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Geolocation API error:', error);
    return null;
  }
};

/**
 * Get country code from dial code
 * @param {string} dialCode - Country dial code (e.g., "+1")
 * @returns {string} Country code (e.g., "US")
 */
export const getCountryCodeFromDialCode = (dialCode) => {
  return DIAL_CODE_TO_COUNTRY[dialCode] || 'US';
};

/**
 * Get country name from geolocation data
 * @param {Object} geoData - Geolocation response data
 * @returns {string} Country name or empty string
 */
export const getCountryFromGeoData = (geoData) => {
  if (geoData && geoData.data && geoData.data.countryName) {
    return geoData.data.countryName;
  }
  return '';
};

/**
 * Submit lead to CRM API
 * @param {Object} formData - Form data containing name, email, country_code, contact_number, message
 * @param {Object} geoLocationData - Geolocation data from getGeoLocation()
 * @param {Object} options - Optional parameters (packageTitle, packageAmount)
 * @returns {Promise<Object>} Response object with success status and data/message
 */
export const submitLead = async (formData, geoLocationData = null, options = {}) => {
  try {
    // Validate required fields
    if (!formData.name || !formData.email || !formData.contact_number || !formData.country_code) {
      return {
        success: false,
        statusCode: 400,
        message: 'Please fill all required fields'
      };
    }

    // Get country code from dial code
    const countryCode = getCountryCodeFromDialCode(formData.country_code);
    
    // Clean phone number - ONLY NUMERIC, no dial codes
    const cleanPhoneNumber = formData.contact_number.replace(/\D/g, '');
    
    // Extract numeric dial code (remove '+' sign)
    const numericDialCode = parseInt(formData.country_code.replace('+', ''));
    
    // Get IP address from geolocation
    const ipAddress = geoLocationData?.data?.ipAddress || '';
    
    // Get city and state from geolocation (optional)
    const city = geoLocationData?.data?.cityName || '';
    const state = geoLocationData?.data?.regionName || '';
    
    // Prepare CRM payload (only include optional fields if they have values)
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: numericDialCode,
      country: countryCode,
      phoneNumber: cleanPhoneNumber, // ONLY NUMERIC
      ipAddress: ipAddress,
      description: formData.message || '',
      pageUrl: window.location.href
    };

    // Add optional fields only if they have values
    if (city) payload.city = city;
    if (state) payload.state = state;
    if (geoLocationData) payload.geoLocation = JSON.stringify(geoLocationData);
    if (options.packageTitle) payload.packageTitle = options.packageTitle;
    if (options.packageAmount) payload.packageAmount = options.packageAmount;

    console.log('📤 Submitting lead to CRM:', payload);
    console.log('🌍 Geolocation data:', geoLocationData);

    // Submit to CRM API with proper headers
    const response = await axios.post(
      'https://leadapi.crmbytes.com/api/Lead/AddLead',
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
          'Referer': window.location.href
        }
      }
    );

    console.log('✅ API Response received:', response.data);

    // Handle 200 Success Response
    if (response.data && response.data.statusCode === 200) {
      console.log('✅ Lead submitted successfully:', response.data);
      return {
        success: true,
        statusCode: 200,
        data: response.data.data,
        customerId: response.data.data?.customerId
      };
    }

    // Handle 401 from API (even though HTTP status is 200)
    if (response.data && response.data.statusCode === 401) {
      console.log('🔒 401 Unauthorized from API:', response.data);
      return {
        success: false,
        statusCode: 401,
        message: response.data.message || response.data.statusName || 'Domain not configured in CRM system'
      };
    }

    // Handle 400 from API (even though HTTP status is 200)
    if (response.data && response.data.statusCode === 400) {
      console.log('⚠️ 400 Bad Request from API:', response.data);
      return {
        success: false,
        statusCode: 400,
        message: response.data.errorMessage || response.data.message || 'Invalid data provided'
      };
    }

    // Handle other responses
    return {
      success: false,
      statusCode: response.data?.statusCode || 500,
      message: response.data?.message || response.data?.errorMessage || response.data?.data?.message || 'Submission failed'
    };

  } catch (error) {
    console.error('❌ Lead submission error:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    
    // Handle API error responses
    if (error.response) {
      const { status, data } = error.response;
      
      // Handle 401 Unauthorized
      if (status === 401) {
        console.error('🔒 401 Unauthorized:', data);
        return {
          success: false,
          statusCode: 401,
          message: data.message || 'Unauthorized - Domain not configured in CRM'
        };
      }
      
      // Handle 400 Bad Request
      if (status === 400) {
        console.error('⚠️ 400 Bad Request - Full response:', JSON.stringify(data, null, 2));
        return {
          success: false,
          statusCode: 400,
          message: data.errorMessage || data.data?.message || data.message || 'Invalid data provided'
        };
      }
      
      // Handle other status codes
      return {
        success: false,
        statusCode: status,
        message: data.message || data.errorMessage || 'An error occurred'
      };
    }
    
    // Network or other errors
    return {
      success: false,
      statusCode: 500,
      message: 'Network error. Please try again.'
    };
  }
};

/**
 * Handle form submission with automatic redirect
 * @param {Object} formData - Form data
 * @param {Object} geoLocationData - Geolocation data
 * @param {Function} navigate - React Router navigate function
 * @param {Function} toast - Toast notification function
 * @param {Object} options - Optional parameters
 */
export const handleFormSubmission = async (formData, geoLocationData, navigate, toast, options = {}) => {
  try {
    const result = await submitLead(formData, geoLocationData, options);
    
    if (result.success && result.statusCode === 200 && result.customerId) {
      // Success - Redirect to application page
      toast?.success?.('Form submitted successfully! Redirecting...');
      navigate(`/application/main?customerid=${result.customerId}`);
      return { success: true };
    } else {
      // Show error message
      const errorMessage = result.message || 'Submission failed. Please try again.';
      toast?.error?.(errorMessage);
      return { success: false, message: errorMessage };
    }
  } catch (error) {
    console.error('Form submission error:', error);
    toast?.error?.('An unexpected error occurred. Please try again.');
    return { success: false, message: 'Unexpected error' };
  }
};

export default {
  getGeoLocation,
  submitLead,
  handleFormSubmission,
  getCountryCodeFromDialCode,
  getCountryFromGeoData
};
